import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortedGuesses } from "../worker/lib/game";
import { api, ApiFailure, type GameState } from "./api";
import { GuessRow } from "./components/GuessRow";
import { ResultSheet } from "./components/ResultSheet";
import { Toast } from "./components/Toast";
import { colorScheme, getInitData, haptic, initTelegram, tg } from "./telegram";

type Screen =
  | { kind: "loading" }
  | { kind: "error"; message: string; retry: boolean }
  | { kind: "game"; state: GameState };

const ERROR_COPY: Record<string, string> = {
  unauthorized: "Відкрий гру через Telegram — кнопка в чаті або t.me/ch1ps_bot/contexto",
  not_configured: "Сервер ще не налаштований. Скажи чіпсу.",
  network: "Нема зв'язку. Спробуй ще раз.",
  upstream: "contexto.me не відповідає. Спробуй за хвилину.",
  server_error: "Щось зламалось. Спробуй ще раз.",
};

export function App() {
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmGiveUp, setConfirmGiveUp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<number | null>(null);

  const showToast = useCallback((msg: string, ms = 1600) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  }, []);

  const load = useCallback(async () => {
    setScreen({ kind: "loading" });
    if (!getInitData()) {
      setScreen({ kind: "error", message: ERROR_COPY.unauthorized ?? "", retry: false });
      return;
    }
    try {
      const state = await api.state();
      setScreen({ kind: "game", state });
      if (state.status !== "playing") setSheetOpen(true);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "server_error";
      setScreen({ kind: "error", message: ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "", retry: code !== "unauthorized" });
    }
  }, []);

  useEffect(() => {
    initTelegram();
    document.documentElement.dataset.scheme = colorScheme();
    const app = tg();
    const onTheme = () => {
      document.documentElement.dataset.scheme = colorScheme();
    };
    app?.onEvent("themeChanged", onTheme);
    void load();
    return () => app?.offEvent("themeChanged", onTheme);
  }, [load]);

  const state = screen.kind === "game" ? screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const sorted = useMemo(() => (state ? sortedGuesses(state.guesses) : []), [state?.guesses]);

  const apply = useCallback(
    async (run: () => Promise<GameState>) => {
      if (busy) return;
      setBusy(true);
      try {
        const next = await run();
        setScreen({ kind: "game", state: next });
        if (next.status !== "playing") {
          if (next.status === "won") haptic.success();
          else haptic.error();
          window.setTimeout(() => setSheetOpen(true), 500);
        } else if (next.last) {
          haptic.tap();
        }
        return next;
      } catch (e) {
        const code = e instanceof ApiFailure ? e.code : "server_error";
        if (code === "unknown_word") {
          haptic.error();
          showToast("Не знаю такого слова");
        } else if (code === "already_guessed") {
          const g = e instanceof ApiFailure ? e.guess : undefined;
          showToast(g ? `Вже було: ${g.w} — ${g.d + 1}` : "Вже було");
        } else if (code === "game_over") {
          void load();
        } else if (code === "bad_request") {
          showToast("Одне слово, літерами");
        } else {
          showToast(ERROR_COPY[code] ?? ERROR_COPY.server_error ?? "", 2200);
        }
        return null;
      } finally {
        setBusy(false);
      }
    },
    [busy, showToast, load],
  );

  const submit = useCallback(async () => {
    const w = word.trim();
    if (!w || !state || finished || busy) return;
    inputRef.current?.focus();
    const next = await apply(() => api.guess(w));
    if (next) setWord("");
  }, [word, state, finished, busy, apply]);

  if (screen.kind === "loading") {
    return <Shell><div className="flex flex-1 items-center justify-center text-tghint">…</div></Shell>;
  }
  if (screen.kind === "error") {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
          <div className="text-base">{screen.message}</div>
          {screen.retry && (
            <button type="button" onClick={() => void load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </div>
      </Shell>
    );
  }

  const s = screen.state;
  const last = s.last;
  return (
    <Shell
      header={
        <header className="flex items-center justify-between px-4 pb-2 pt-[calc(var(--tg-content-safe-area-inset-top,0px)+10px)]">
          <div className="text-xs text-tghint">#{s.number}</div>
          <div className="text-lg font-extrabold tracking-[0.2em]">CONTEXTO</div>
          <button type="button" onClick={() => finished && setSheetOpen(true)} className="text-xs text-tghint">
            🔥 {s.me.streak}
          </button>
        </header>
      }
    >
      <div className="px-4 pb-1 text-xs text-tghint">
        спроб: <b className="text-tgtext">{s.guesses.length}</b>
        {s.tips > 0 ? <> · підказок: <b className="text-tgtext">{s.tips}</b></> : null}
        {finished && s.answer ? <> · слово: <b className="text-tgtext">{s.answer}</b></> : null}
      </div>

      <form
        className="flex gap-2 px-4 pb-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <input
          ref={inputRef}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          readOnly={finished}
          placeholder={finished ? "гра закінчена" : "введи слово"}
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="send"
          inputMode="text"
          className="h-[46px] flex-1 rounded-xl border border-tghint/30 bg-transparent px-4 text-[16px] text-tgtext outline-none placeholder:text-tghint/60 focus:border-tgbutton disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={finished || busy || !word.trim()}
          // Keep the keyboard up: don't let the button take focus from the input.
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          onTouchEnd={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="h-[46px] rounded-xl bg-tgbutton px-5 text-[15px] font-bold text-tgbuttontext active:brightness-90 disabled:opacity-35"
        >
          {busy ? "…" : "→"}
        </button>
      </form>

      <div
        className="flex-1 overflow-y-auto px-4 pb-3 pt-1"
        onTouchMove={() => inputRef.current?.blur()}
      >
        {last && !finished && (
          <div className="mb-3">
            <GuessRow guess={last} pinned />
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="mt-10 text-center text-sm text-tghint">
            Вгадай секретне слово. Число = наскільки близько по змісту. 1 — це воно.
          </div>
        ) : (
          <div className="space-y-[6px]">
            {sorted.map((g) => (
              <GuessRow key={g.w} guess={g} />
            ))}
          </div>
        )}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => void apply(() => api.tip())}
            className="flex-1 rounded-xl bg-tghint/15 py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
          >
            💡 підказка
          </button>
          {confirmGiveUp ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmGiveUp(false);
                void apply(() => api.giveUp());
              }}
              className="flex-1 rounded-xl bg-bar-red/80 py-3 text-sm font-semibold text-white active:opacity-70"
            >
              точно здатись?
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setConfirmGiveUp(true);
                window.setTimeout(() => setConfirmGiveUp(false), 3000);
              }}
              className="flex-1 rounded-xl bg-tghint/15 py-3 text-sm font-semibold active:opacity-70 disabled:opacity-40"
            >
              🏳 здатись
            </button>
          )}
        </div>
      )}

      <Toast message={toast} />
      {sheetOpen && finished && <ResultSheet state={s} onDismiss={() => setSheetOpen(false)} />}
    </Shell>
  );
}

function Shell({ header, children }: { header?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[560px] flex-col bg-tgbg text-tgtext"
      style={{ height: "var(--tg-viewport-stable-height, 100vh)", minHeight: "100%" }}
    >
      {header}
      {children}
    </div>
  );
}
